import { useLang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";
import {
  buildMergedUploadedSections,
  getCategoryUploadStatus,
  resolveQualifyingRoute,
  shouldShowEmploymentDocCategory,
  shouldShowFamilyDocCategory,
  shouldShowCriminalRecordDocCategory,
  type DocChecklistCategory,
} from "../utils/deriveDocumentChecklistHints";
import Footer from "./Footer";

type PreviewCategory = "identity" | "residence" | "criminal" | "asylum" | "employment" | "family" | "vulnerability" | "other";

export default function CompletionScreen({
  onNavigate,
}: {
  onNavigate: (view: "documents" | "organizer", category?: PreviewCategory) => void;
}) {
  const { t, isRTL } = useLang();
  const { data } = useForm();

  const sections = buildMergedUploadedSections(data);
  const qualifyingRoute = resolveQualifyingRoute(data.qualifyingGround, data.subRoute, data.irregularSubRoute);
  const isAsylumApplied = String(data.asylumApplicant || "").trim().toLowerCase() === "yes" || data.asylumRoutingConfirmed === true;

  const categories: Array<{ key: PreviewCategory; labelKey: string }> = [
    { key: "identity", labelKey: "docFlow.catIdentity" },
    { key: "residence", labelKey: "docFlow.catResidence" },
  ];

  if (shouldShowCriminalRecordDocCategory(data.hasCriminalRecord)) {
    categories.push({ key: "criminal", labelKey: "docFlow.catCriminal" });
  }
  if (isAsylumApplied) {
    categories.push({ key: "asylum", labelKey: "docFlow.catAsylum" });
  } else {
    if (shouldShowEmploymentDocCategory(qualifyingRoute)) {
      categories.push({ key: "employment", labelKey: "docFlow.catEmployment" });
    } else if (shouldShowFamilyDocCategory(qualifyingRoute)) {
      categories.push({ key: "family", labelKey: "docFlow.catFamily" });
    } else if (qualifyingRoute === "vulnerability") {
      categories.push({ key: "vulnerability", labelKey: "docFlow.catVulnerability" });
    }
  }
  categories.push({ key: "other", labelKey: "docFlow.catOther" });

  const getStatus = (cat: PreviewCategory): "complete" | "inProgress" | "notStarted" => {
    if (cat === "identity") {
      return getCategoryUploadStatus("identity", sections, data);
    }
    if (cat === "criminal") {
      return getCategoryUploadStatus("criminal", sections, data);
    }
    if (cat === "vulnerability") {
      return getCategoryUploadStatus("vulnerability", sections, data);
    }
    return getCategoryUploadStatus(cat as DocChecklistCategory, sections, data);
  };

  const statusClassName = (status: "complete" | "inProgress" | "notStarted", isOptional: boolean): string => {
    if (isOptional && status === "notStarted") return "bg-slate-100 text-slate-500";
    if (status === "complete") return "bg-emerald-100 text-emerald-700";
    if (status === "inProgress") return "bg-amber-100 text-amber-700";
    return "bg-slate-100 text-slate-500";
  };

  const firstIncompleteRequiredCategory =
    categories.find((cat) => cat.key !== "other" && getStatus(cat.key) !== "complete")?.key ??
    categories.find((cat) => getStatus(cat.key) !== "complete")?.key ??
    "identity";

  const handleChecklistOpen = (cat: PreviewCategory) => {
    if (cat === "other") {
      onNavigate("organizer");
      return;
    }
    onNavigate("documents", cat);
  };

  return (
    <div
      className="min-h-[calc(100vh-56px)] bg-[#F8F7F4] flex flex-col"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="flex-1 py-8">
        <div className="max-w-[480px] mx-auto px-4">
          <h1 className="text-[18px] font-semibold text-slate-900 mb-4">
            {t("docFlow.title")}
          </h1>

          <button
            type="button"
            onClick={() => onNavigate("documents", firstIncompleteRequiredCategory)}
            className="flex items-center gap-1.5 text-[13px] text-slate-500 font-medium cursor-pointer hover:text-slate-700 transition-colors mb-4"
          >
            <i className="ri-image-line text-sm" />
            {t("docFlow.photoTipsLink")}
          </button>

          <div className="flex flex-col gap-3 mb-8">
            {categories.map((cat) => {
              const status = getStatus(cat.key);
              const isOptional = cat.key === "other" && status === "notStarted";
              const statusLabel = isOptional ? t("optional") : t(`docFlow.${status}`);
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => handleChecklistOpen(cat.key)}
                  className="w-full bg-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-sm px-4 py-4 flex items-center gap-4 text-left cursor-pointer transition-all group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-slate-800 leading-snug">
                      {t(cat.labelKey)}
                    </p>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${statusClassName(status, isOptional)}`}
                  >
                    {statusLabel}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-arrow-right-s-line text-slate-400 text-lg group-hover:text-emerald-500 transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => onNavigate("documents", firstIncompleteRequiredCategory)}
              className="w-full h-12 flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              {t("docFlow.letsBegin")}
            </button>

            <button
              type="button"
              onClick={() => onNavigate("organizer")}
              className="w-full h-12 flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[14px] cursor-pointer transition-colors whitespace-nowrap shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            >
              {t("docFlow.goToOrganizer")}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
